import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchAppointment,
  updateAppointment,
  deleteAppointment,
  listAppointments,
} from "../redux/actions/appointmentActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { fetchFiles } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";

import {
  Container,
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Breadcrumbs,
  Link,
} from "@mui/material";

import { useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { textFieldStyles } from "../styles/textFieldStyles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import enUS from "date-fns/esm/locale/en-US";
import format from "date-fns/esm/format";
import parse from "date-fns/esm/parse";
import startOfWeek from "date-fns/esm/startOfWeek";
import getDay from "date-fns/esm/getDay";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/esm/locale/en-GB";

const getBreadcrumbs = (t) => [
  { label: t('common:home'), href: "/" },
  { label: t('common:files'), href: "/files" },
];
const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

// appointmentTypes will be moved to translation files

// appointmentColours will be moved to translation files

const ViewEditAppointmentPage = () => {
  const { t, i18n } = useTranslation(['common', 'pages', 'forms', 'buttons']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const { id } = useParams();
  const { appointment } = useSelector((state) => state.appointmentDetails);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const { appointments } = useSelector((state) => state.appointmentList);

  const [owner, setOwner] = useState("");
  const [type, setType] = useState("");
  const [calendarType, setCalendarType] = useState("Appointment");
  const [caseId, setCaseId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reminder, setReminder] = useState("");
  const [locationField, setLocationField] = useState("");
  const [colour, setColour] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [userRelationship, setUserRelationship] = useState(null);

  const locales = {
    "en-US": enUS,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('common:home'), href: "/" },
    { label: t('common:files'), href: "/files" },
  ]);

  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    console.log("id is", locationCaseId);
    
    // First try to get case ID from location state
    let caseIdToUse = locationCaseId;
    
    // If no case ID in location state, try to get it from the appointment data
    if (!caseIdToUse && appointment?.case) {
      caseIdToUse = appointment.case._id || appointment.case;
    }
    
    console.log("files ", files);
    console.log("case ID to use:", caseIdToUse);
    
    if (caseIdToUse && files.length > 0) {
      const initialCaseFile = files.find((file) => file._id === caseIdToUse);
      console.log("case file is", initialCaseFile);
      
      if (initialCaseFile) {
                 setBreadcrumbs([
           { label: t('common:home'), href: "/" },
           { label: t('common:files'), href: "/files" },
           {
             label: initialCaseFile.name || t('common:case_file'),
             href: `/files/${initialCaseFile._id}/overview`,
           },
         ]);
      }
    }
  }, [files, id, t, appointment, location.state]);

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
      setOwner(user.relationship);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchAppointment(id));
    dispatch(fetchRelationships());
    dispatch(fetchFiles());
    dispatch(listAppointments());
  }, [dispatch, id]);

  useEffect(() => {
    if (appointment) {
      setOwner(appointment.owner || "");
      setType(appointment.type || "");
      setCaseId(appointment.case || "");
      setSubject(appointment.subject || "");
      setDescription(appointment.description || "");
      setStartDate(
        appointment.startDate ? new Date(appointment.startDate) : ""
      );
      setEndDate(appointment.endDate ? new Date(appointment.endDate) : "");
      setReminder(appointment.reminder || "");
      setLocationField(appointment.location || "");
      setColour(appointment.colour || "");
    }
  }, [appointment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedAppointment = {
      _id: id,
      owner,
      type,
      case: caseId,
      calendarType,
      subject,
      description,
      startDate,
      endDate,
      reminder,
      location: locationField,
      colour,
    };
    dispatch(updateAppointment(updatedAppointment));
    setTimeout(() => {
      // Navigate back to the case file if available, otherwise use navigate(-1)
      if (appointment?.case) {
        const caseId = appointment.case._id || appointment.case;
        navigate(`/files/${caseId}/overview`);
      } else {
        navigate(-1);
      }
    }, 1000);
  };

  const handleDelete = () => {
    dispatch(deleteAppointment(id));
    // Navigate back to the case file if available, otherwise to calendar
    if (appointment?.case) {
      const caseId = appointment.case._id || appointment.case;
      navigate(`/files/${caseId}/overview`);
    } else {
      navigate("/calendar");
    }
  };

  const handleCancel = () => {
    // Navigate back to the case file if available, otherwise to calendar
    if (appointment?.case) {
      const caseId = appointment.case._id || appointment.case;
      navigate(`/files/${caseId}/overview`);
    } else {
      navigate("/calendar");
    }
  };

  const inputStyles = {
    width: "30%",
    marginBottom: 2,
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });
  const [filteredAppointments, setFilteredAppointments] = useState([]);

  useEffect(() => {
    const filtered = appointments
      .map((appointment) => ({
        ...appointment,
        isNew: false,
      }))
      .filter(
        (appointment) =>
          new Date(appointment.startDate).toDateString() ===
            new Date(startDate).toDateString() &&
          appointment?._id !== id &&
          appointment.owner?._id.toString() === user?.relationship.toString()
      );

    if (startDate && endDate) {
      const newAppointment = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        owner: user?.relationship,
        isNew: true, // Indicate that this is a new appointment
        // Add other necessary fields for the new appointment
      };
      // console.log(newAppointment);
      filtered.push(newAppointment);
    }

    setFilteredAppointments(filtered);
  }, [startDate, endDate, appointments]);

    // Custom date formatting function
  const formatCustomDate = (date) => {
    const dateObj = new Date(date);
    const weekday = dateObj.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { weekday: 'long' });
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { month: 'long' });
    const year = dateObj.getFullYear();
    
    return `${weekday}, ${day} ${month}, ${year}`;
  };

  const CustomToolbar = () => (
    <div className="rbc-toolbar">
      <span className="rbc-toolbar-label">
        {startDate
          ? formatCustomDate(startDate)
          : formatCustomDate(new Date())}
      </span>
    </div>
  );

  useEffect(() => {
    console.log(appointment);
  }, [appointment]);

  const convertToDate = (date) => {
    return typeof date === "string" ? new Date(date) : date;
  };
  const formats = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, "HH:mm", culture),
    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, "HH:mm", culture)} - ${localizer.format(
        end,
        "HH:mm",
        culture
      )}`,
  };

  const eventPropGetter = (event) => {
    const className = event.isNew ? "rbc-event-new" : "rbc-event-filtered";
    return { className };
  };

  const shouldDisableMinutes = (minutes) => {
    return minutes % 15 !== 0;
  };

  useEffect(() => {
    const handlePopupOpen = () => {
      setTimeout(() => {
        const disabledMenuItems = document.querySelectorAll(
          "li.MuiButtonBase-root.MuiMenuItem-root.Mui-disabled"
        );

        disabledMenuItems.forEach((item) => {
          // Perform your actions here
          item.classList.add("hidden-option"); // Example: Add a class to hide the element
        });
      }, 100);
    };

    document.addEventListener("click", handlePopupOpen);

    return () => {
      document.removeEventListener("click", handlePopupOpen);
    };
  }, []);

  useEffect(() => {
    resetScrollTop();
  }, [startDate, endDate]);

  const resetScrollTopWait = () => {
    setTimeout(() => {
      const secondClockSection = document.querySelector(
        ".MuiMultiSectionDigitalClockSection-root:nth-of-type(2)"
      );
      const thirdClockSection = document.querySelector(
        ".MuiMultiSectionDigitalClockSection-root:nth-of-type(3)"
      );
  
      if (secondClockSection) {
        secondClockSection.classList.add('smooth-scroll');
        secondClockSection.scrollTop = 0;
      }
      if (thirdClockSection) {
        thirdClockSection.classList.add('smooth-scroll');
        thirdClockSection.scrollTop = 0;
      }
  
      // Remove the smooth-scroll class after the transition
      setTimeout(() => {
        if (secondClockSection) {
          secondClockSection.classList.remove('smooth-scroll');
        }
        if (thirdClockSection) {
          thirdClockSection.classList.remove('smooth-scroll');
        }
      }, 1000); // Adjust the timeout to match the transition duration
    }, 1000);
  };
  
  

  const resetScrollTop = () => {
    const secondClockSection = document.querySelector(
      ".MuiMultiSectionDigitalClockSection-root:nth-of-type(2)"
    );
    const thirdClockSection = document.querySelector(
      ".MuiMultiSectionDigitalClockSection-root:nth-of-type(3)"
    );

    if (secondClockSection) {
      secondClockSection.scrollTop = 0;
    }
    if (thirdClockSection) {
      thirdClockSection.scrollTop = 0;
    }
  };

  // Usage
  useEffect(() => {
    resetScrollTop();
  }, []);

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
             <BreadcrumbNav
         title={
           appointment?.calendarType === "Appointment"
             ? t('pages:calendar.edit_appointment')
             : t('pages:calendar.edit_hearing')
         }
         breadcrumbs={breadcrumbs}
         showBreadcrumbs={true}
       />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
          width: "95%",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <style>
          {`
          .smooth-scroll {
        scroll-behavior: smooth;
      }
          .hidden-option {
            display: none !important;
          }
            /* Custom scrollbar styles */
          .MuiMultiSectionDigitalClockSection-root:first-of-type::-webkit-scrollbar {
            width: 6px; /* Adjust the width of the scrollbar */
          }
            .MuiMultiSectionDigitalClockSection-root:nth-of-type(2),
    .MuiMultiSectionDigitalClockSection-root:nth-of-type(3) {
      overflow: hidden; /* Disable the scrollbar */
    }
          .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2); /* Adjust the color of the scrollbar thumb */
            border-radius: 0; /* Adjust the roundness of the scrollbar thumb */
          }
          .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track {
            background-color: transparent; /* Adjust the color of the scrollbar track */
          }
        `}
        </style>
        <form onSubmit={handleSubmit} style={{ width: "80%" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ width: "100%" }}>
              <FormControl variant="standard" sx={{ mr: 3, width: "44.5%" }}>
                <CustomAutocomplete
                  options={relationships}
                  value={
                    relationships?.find((rel) => rel._id === owner._id) || null
                  }
                  onChange={(event, newValue) =>
                    setOwner(newValue ? newValue._id : "")
                  }
                  label={t('forms:labels.owner')}
                  filterType={t('forms:filterTypes.member')}
                  addNewLabel={t('forms:addLabels.addOwner')}
                  addNewPath="/relationships/create"
                />
              </FormControl>
            </Box>
            <Box sx={{ width: "98.5%", display: "flex" }}>
              <Autocomplete
                options={t('pages:appointments.types', { returnObjects: true })}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label || option}
                value={type}
                onChange={(event, newValue) => setType(typeof newValue === 'string' ? newValue : newValue?.label || "")}
                renderInput={(params) => <TextField {...params} label={t('forms:labels.type')} />}
                sx={textFieldStyles({ width: "45%" })}
                size="small"
              />
              <FormControl sx={{ width: "45%" }}>
                <FileAutocomplete
                  options={filteredFiles}
                  value={
                    filteredFiles.find((file) => file._id === caseId._id) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    setCaseId(newValue ? newValue._id : "")
                  }
                  label={t('forms:labels.case')}
                  name="case"
                />
              </FormControl>
            </Box>
            <TextField
              label={t('forms:labels.subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <TextField
              label={t('forms:labels.description')}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <Box
              sx={{
                width: "95%",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}
              adapterLocale={enGB}>
                <DateTimePicker
                  value={startDate}
                  size="small"
                  required
                  onChange={(newValue) =>( setStartDate(newValue), setEndDate(newValue))}
                  onOpen={resetScrollTopWait}
                  inputFormat="dd/MM/yyyy HH:mm"
                  renderInput={(params) => (
                    <TextField {...params} fullWidth required size="small" />
                  )}
                  slotProps={{
                    textField: {
                      fullWidth: true,

                      sx: textFieldStyles({ width: "30%" }),
                      size: "small",
                      label: t('forms:labels.start_date'),
                    },
                  }}
                  minutesStep={15}
                  ampm = {false}
                />
                <DateTimePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  onOpen={resetScrollTopWait}
                  inputFormat="dd/MM/yyyy HH:mm"
                  renderInput={(params) => (
                    <TextField {...params} fullWidth required size="small" />
                  )}
                  slotProps={{
                    textField: {
                      fullWidth: true,

                      sx: textFieldStyles({ width: "30%" }),
                      size: "small",
                      label: t('forms:labels.end_date'),
                    },
                  }}
                  minutesStep={15}
                  ampm = {false}
                />
              </LocalizationProvider>
              <TextField
                label={t('pages:appointments.reminder_days')}
                type="number"
                fullWidth
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                sx={textFieldStyles({ width: "30%" })}
                size="small"
              />
            </Box>

            <TextField
              label={t('forms:location')}
              value={locationField}
              onChange={(e) => setLocationField(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <FormControl sx={textFieldStyles({ width: "44.5%" })} size="small">
              <InputLabel id="colour-select-label">{t('forms:colour')}</InputLabel>
              <Select
                labelId="colour-select-label"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                label={t('forms:colour')}
              >
                {t('forms:colours', { returnObjects: true }).map((appointmentColour) => (
                  <MenuItem
                    key={appointmentColour.name}
                    value={appointmentColour.name}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: appointmentColour.color,
                          marginRight: 1,
                        }}
                      />
                      {appointmentColour.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              // padding: "10px 16px",
              // background: "#fff",
              zIndex: 10,
              backgroundColor: "white",
              color: "#000000",
              padding: "13px 80px",
            }}
          >
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              size="small"
            >
              {t('buttons:secondary.cancel')}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} size="small">
              {t('buttons:primary.save')}{" "}
              {appointment?.calendarType === "Appointment"
                ? t('pages:appointments.appointment')
                : t('pages:hearings.hearing')}
            </SubmitButton>

            {/* <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            size="small"
            sx={buttonStyles}
          >
            Delete Appointment
          </Button> */}
          </Box>
        </form>
        <Box sx={{ width: "30%" }}>
          <Calendar
            localizer={localizer}
            events={filteredAppointments.map((event) => ({
              ...event,
              startDate: convertToDate(event.startDate),
              endDate: convertToDate(event.endDate),
            }))}
            startAccessor="startDate"
            endAccessor="endDate"
            eventPropGetter={eventPropGetter}
            style={{ height: 500 }}
            views={["day"]}
            defaultView="day"
            date={startDate ? startDate : new Date()} // Check if startDate is empty
            components={{
              toolbar: CustomToolbar,
            }}
            formats={formats}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default ViewEditAppointmentPage;
